import { Space } from "antd";
import type { SortOrder } from "antd/es/table/interface";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import PermissionCheck from "~Common/PermissionCheck";
import type { ConductTypes } from "~enums/conductTypes.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { Competition } from "~types/competition.ts";
import type { Person } from "~types/person.ts";
import type { Season } from "~types/season.ts";
import DeleteConductAction from "../components/DeleteConductAction.tsx";
import UpdateConductAction from "../components/UpdateConductAction.tsx";
import type { UpsertConductData } from "../types/upsertConductData.ts";

const useConductTableColumns = (
  onConductDelete: (conductId: string) => void,
  onConductUpsert: (upsertData: UpsertConductData) => void,
  isUpsertInProcess: boolean,
) => {
  const { formatMessage } = useIntl();

  return [
    {
      title: formatMessage({ id: "personname" }),
      dataIndex: "person",
      render: (person: Person) => <div>{person ? person.nameFullLocal : ""}</div>,
    },
    {
      title: formatMessage({ id: "person.role" }),
      dataIndex: "role",
      width: 160,
      render: (role: string | null) => <div>{role ? role : formatMessage({ id: "conduct.role.player" })}</div>,
    },
    {
      title: formatMessage({ id: "competitionname" }),
      dataIndex: "competition",
      width: 200,
      render: (competition: Competition) => <div>{competition ? competition.nameLocal : ""}</div>,
    },
    {
      title: formatMessage({ id: "seasonname" }),
      dataIndex: "season",
      width: 200,
      render: (season: Season) => <div>{season ? season.nameLocal : ""}</div>,
    },
    {
      title: formatMessage({ id: "fixture.id" }),
      dataIndex: "fixtureId",
      width: 200,
    },
    {
      title: formatMessage({ id: "conduct.type" }),
      dataIndex: "conductType",
      width: 140,
      render: (conductType: Array<ConductTypes>) => (
        <div>
          {conductType.map((item: ConductTypes) => (
            <div key={item}>{item}</div>
          ))}
        </div>
      ),
    },
    {
      title: formatMessage({ id: "date.added" }),
      dataIndex: "added",
      width: 115,
      sorter: true,
      defaultSortOrder: "descend" as SortOrder,
      sortDirections: ["descend", "ascend", "descend"] as SortOrder[],
      render: (value: string) => <div>{dayjs(value).format("YYYY-MM-DD HH:mm:ss")}</div>,
    },
    {
      title: formatMessage({ id: "date.updated" }),
      dataIndex: "updated",
      width: 115,
      sorter: true,
      sortDirections: ["descend", "ascend", "descend"] as SortOrder[],
      render: (value: string) => <div>{dayjs(value).format("YYYY-MM-DD HH:mm:ss")}</div>,
    },
    {
      title: formatMessage({ id: "status" }),
      dataIndex: "status",
      width: 50,
    },
    {
      render: (_: any, record: any) => (
        <Space style={{ columnGap: "5px" }}>
          <PermissionCheck permissionKeys={[PermissionNames.CONDUCT]} permissionOptions={[PermissionOperations.UPDATE]}>
            <UpdateConductAction conduct={record} onUpdate={onConductUpsert} isUpdateInProcess={isUpsertInProcess} />
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionNames.CONDUCT]} permissionOptions={[PermissionOperations.DELETE]}>
            <DeleteConductAction conduct={record} onDelete={onConductDelete} />
          </PermissionCheck>
        </Space>
      ),
      width: 50,
    },
  ];
};

export default useConductTableColumns;
